import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import { FileTagsVisibility } from "@mapmycustomers/shared";
import Visibility from "@mapmycustomers/shared/enum/Visibility";
import FileTag from "@mapmycustomers/shared/types/entity/FileTag";
import Team from "@mapmycustomers/shared/types/Team";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, EditableText, Popconfirm, VisibilitySelector } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import MoreButton from "@app/component/MoreButton";
import { getCurrentUser } from "@app/store/iam";
import { isManager, isMember, isOwner } from "@app/store/iam/util";
import { getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { userDisplayName } from "@app/util/formatters";

import { bulkDeleteFileTags } from "../../store/actions";
import { ALL_VISIBILITIES } from "../../util/const";

import getVisibilityDisplayName from "./getVisibilityDisplayName";
import styles from "./TagItem.module.scss";

const messages = defineMessages({
  creator: {
    id: "settings.fileTags.tag.creator",
    defaultMessage: "<b>Creator:</b> {name}",
    description: "Tag creator name",
  },
  delete: {
    id: "settings.fileTags.tag.delete",
    defaultMessage: "Delete",
    description: "Delete dropdown button",
  },
  deleteConfirm: {
    id: "settings.fileTags.list.delete.confirm",
    defaultMessage: "Are you sure you want to delete this tag? This action cannot be undone.",
    description: "Delete confirm message for tag",
  },
  edit: {
    id: "settings.fileTags.tag.edit",
    defaultMessage: "Edit",
    description: "Edit dropdown button",
  },
  private: {
    id: "settings.fileTags.tag.visibility.private",
    defaultMessage: "Private",
    description: "Private option for file tag visibility",
  },
  sharedByAdmin: {
    id: "settings.fileTags.visibility.tooltip",
    defaultMessage: "Shared by your admin",
    description: "Tooltip message if file tag is shared by admin",
  },
  shareWith: {
    id: "settings.fileTags.shareWith",
    defaultMessage: "Share With",
    description: "Share with text for sharing",
  },
});

type TagItemProps = {
  onChange: (tag: FileTag) => void;
  onSelect: (tagId: FileTag["id"]) => void;
  selected: boolean;
  tag: FileTag;
} & typeof mapDispatchToProps &
  ReturnType<typeof mapStateToProps>;

const TagItem: React.FC<TagItemProps> = ({
  currentUser,
  onBulkDeleteFileTags,
  onChange,
  onSelect,
  selected,
  tag,
  teams,
}) => {
  const intl = useIntl();
  const [isEditing, startEditing, stopEditing] = useBoolean();

  const disabled = currentUser.id !== tag.userId && !isOwner(currentUser);

  const handleVisibilityChange = useCallback(
    (visibility: FileTagsVisibility, teamIds?: Team["id"][]) => {
      onChange({ ...tag, teamId: teamIds ?? [], visibility });
    },
    [onChange, tag]
  );

  const handleDelete = useCallback(() => {
    onBulkDeleteFileTags({ ids: [tag.id] });
  }, [onBulkDeleteFileTags, tag.id]);

  const items: MenuProps["items"] = useMemo(
    () => [
      {
        key: "edit",
        label: intl.formatMessage(messages.edit),
        onClick: startEditing,
      },
      {
        key: "delete",
        label: (
          <Popconfirm
            danger
            description={intl.formatMessage(messages.deleteConfirm)}
            onConfirm={handleDelete}
            overlayClassName={styles.popconfirm}
          >
            <span onClick={stopEvents}>{intl.formatMessage(messages.delete)}</span>
          </Popconfirm>
        ),
      },
    ],
    [intl, handleDelete, startEditing]
  );

  const handleChangeName = useCallback(
    (value: string) => {
      onChange({ ...tag, name: value });
      stopEditing();
    },
    [onChange, stopEditing, tag]
  );

  const handleSelect = useCallback(() => {
    onSelect(tag.id);
  }, [onSelect, tag.id]);

  const allowedVisibilities = useMemo(
    () =>
      isOwner(currentUser)
        ? ALL_VISIBILITIES
        : isManager(currentUser)
        ? [Visibility.PRIVATE, Visibility.SHARED_WITH_TEAM]
        : [Visibility.PRIVATE],
    [currentUser]
  );

  const getCustomLabel = useCallback(
    (visibility: Visibility, defaultLabel: string) => {
      return visibility === Visibility.PRIVATE
        ? intl.formatMessage(messages.private)
        : defaultLabel;
    },
    [intl]
  );

  return (
    <div className={styles.container}>
      <Checkbox
        checked={selected}
        className={styles.checkbox}
        disabled={disabled}
        onChange={handleSelect}
      />
      {isEditing ? (
        <EditableText
          className={styles.nameEdit}
          editing
          onChange={handleChangeName}
          onEditingChange={stopEditing}
          size="small"
          value={tag.name}
        />
      ) : (
        <span className={styles.name}>{tag.name}</span>
      )}
      <div className={styles.actions}>
        <Tag>
          {intl.formatMessage(messages.creator, {
            b: (text) => <b>{text}</b>,
            name: userDisplayName(tag.user),
          })}
        </Tag>
        <VisibilitySelector
          allowedVisibilities={allowedVisibilities}
          disabled={disabled || isMember(currentUser)}
          getLabel={getCustomLabel}
          onChange={handleVisibilityChange}
          selectedTeams={tag.teamId}
          subLabel={intl.formatMessage(messages.shareWith)}
          teams={teams}
          userRole={currentUser.role.key}
          value={tag.visibility}
        >
          <Tooltip
            title={
              disabled && tag.visibility !== Visibility.PRIVATE
                ? intl.formatMessage(messages.sharedByAdmin)
                : undefined
            }
          >
            <div>
              <Button
                disabled={disabled || isMember(currentUser)}
                rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
                type="control"
              >
                {getVisibilityDisplayName(
                  intl,
                  tag.visibility,
                  currentUser.role.key,
                  tag.teamId.length
                )}
              </Button>
            </div>
          </Tooltip>
        </VisibilitySelector>
        <Dropdown disabled={disabled} menu={{ items }} trigger={["click"]}>
          <MoreButton />
        </Dropdown>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUser: getCurrentUser(state)!,
  teams: getTeams(state),
});

const mapDispatchToProps = {
  onBulkDeleteFileTags: bulkDeleteFileTags.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(TagItem);
